import {useEffect, useRef} from 'react';

interface Props {
  value: string;
  /** Called on blur, only when the text actually changed. */
  onCommit(next: string): void;
  /** Optional live updates, for fields whose value is read before any blur happens. */
  onInput?(next: string): void;
  className?: string;
  placeholder?: string;
  id?: string;
  dir?: 'rtl' | 'ltr';
}

/**
 * An inline-editable region.
 *
 * React deliberately does not own the text: making a `contentEditable` a controlled
 * component means every keystroke re-renders it, which collapses the caret to the start
 * of the node. Instead the DOM holds the text, and the incoming value is written back
 * only when it changes *and* the node is not focused — so a background project reload
 * can never yank text out from under someone mid-sentence.
 *
 * Committing on blur (rather than on every keystroke) is also what keeps the activity
 * log readable: one entry per edit, not one per character.
 */
export function Editable({value, onCommit, onInput, className, placeholder, id, dir}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Read in the blur handler without making it depend on the latest render's closure.
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el && el.innerText !== value) el.innerText = value;
  }, [value]);

  return (
    <div
      id={id}
      ref={ref}
      dir={dir}
      className={className}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      role="textbox"
      tabIndex={0}
      data-placeholder={placeholder}
      onInput={() => {
        if (onInput && ref.current) onInput(ref.current.innerText);
      }}
      onBlur={() => {
        const el = ref.current;
        if (!el) return;
        // Non-breaking spaces creep in from contenteditable; normalise before comparing.
        const next = el.innerText.replace(/ /g, ' ').trim();
        if (next !== latest.current) onCommit(next);
      }}
    />
  );
}
